import { HttpClient } from '@angular/common/http';
import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface User {
  id: number;
  email: string;
  nombre: string;
  rol: string;
  /** Claves de módulo a las que el rol da acceso. `'*'` ⇒ acceso total
   * (admin). Viene en el login response y en `/auth/me`. */
  modulos?: string[];
  /** Fuerza el cambio de contraseña en el primer login (contraseña temporal). */
  debeCambiarPassword?: boolean;
}

interface TokenResponse {
  accessToken: string;
  tokenType: string;
  user: User;
}

const TOKEN_KEY = 'invertus:auth:token';
const USER_KEY = 'invertus:auth:user';
/**
 * apiUrl con el que se emitió el token guardado. Nos protege del caso
 * "arranqué en start:local, después arranqué en start:dev" — como los
 * JWT_SECRET_KEY del backend son distintos por entorno, el token viejo
 * genera 401 y el interceptor loguea afuera. Detectando el switch de
 * apiUrl al arrancar el AuthService, limpiamos el token antes de que se
 * intente usar.
 */
const API_URL_KEY = 'invertus:auth:apiUrl';

/** Cuánto antes del exp del JWT consideramos que la sesión está “a punto de
 *  vencer”. Damos 5 s de gracia para evitar requests que crucen el borde. */
const EXPIRY_LEEWAY_MS = 5_000;

/** Cada cuánto el servicio verifica si la sesión sigue viva (auto-logout). */
const SESSION_CHECK_INTERVAL_MS = 30_000;

/**
 * Servicio de autenticación.
 *
 * - `login()` consume `POST /auth/login` y persiste token + user en localStorage.
 * - El interceptor (`AuthInterceptor`) lee el token y lo agrega como Bearer.
 * - El guard (`authGuard`) protege las rutas autenticadas.
 * - `currentUser` es un signal reactivo que la UI puede leer (header, etc.).
 * - El servicio se auto-verifica cada 30 s: si el JWT venció, dispara logout
 *   y redirige al login (evita el estado "zombie" en el sidebar).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _user = signal<User | null>(this.detectEnvSwitch() ? null : this.loadUser());
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(
    () => this._user() !== null && !this.isTokenExpired(),
  );
  /** Módulos del usuario actual (según su rol). Vacío si no hay sesión. */
  readonly modulos = computed<string[]>(() => this._user()?.modulos ?? []);

  /**
   * True si el usuario tiene acceso al módulo `clave`. Un usuario admin lleva
   * `'*'` en `modulos` ⇒ acceso a todos los módulos. El backend revalida por
   * request (no confía en esto), así que sirve para filtrar UI, no como
   * control de seguridad.
   */
  tieneModulo(clave: string): boolean {
    const mods = this._user()?.modulos ?? [];
    return mods.includes('*') || mods.includes(clave);
  }

  /**
   * True si el usuario es administrador: su rol lleva `'*'` en `modulos`
   * (acceso total). Es el gate de las pantallas de gestión de usuarios/roles,
   * que el backend protege con `require_admin`. Mantener consistente con el
   * backend: al no estar `'usuarios'` en el catálogo de módulos, gatear por
   * este flag (no por `tieneModulo('usuarios')`, que un rol viejo con esa
   * clave heredada aún pasaría).
   */
  esAdmin(): boolean {
    return (this._user()?.modulos ?? []).includes('*');
  }

  /** Limpia el flag de cambio obligatorio tras un cambio exitoso (para que el
   * guard deje de redirigir sin necesidad de re-login). */
  markPasswordChanged(): void {
    const u = this._user();
    if (!u) return;
    const actualizado: User = { ...u, debeCambiarPassword: false };
    localStorage.setItem(USER_KEY, JSON.stringify(actualizado));
    this._user.set(actualizado);
  }

  constructor() {
    // Auto-logout cuando el JWT vence en background. Cualquier expiración
    // entre clicks ya no deja links zombi en el sidebar.
    const intervalId = window.setInterval(() => {
      if (this._user() !== null && this.isTokenExpired()) {
        this.logout(true);
      }
    }, SESSION_CHECK_INTERVAL_MS);
    this.destroyRef.onDestroy(() => window.clearInterval(intervalId));

    // Si el user cambia a null por logout externo (otra pestaña, por ejemplo),
    // limpiar storage también acá.
    effect(() => {
      if (this._user() === null && localStorage.getItem(TOKEN_KEY)) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    });

    // Al arrancar con una sesión válida en storage, refrescar el user desde
    // el backend (`GET /auth/me`): el login congela `modulos` y
    // `debeCambiarPassword` en localStorage y un cambio de rol no se vería
    // hasta re-login. Se difiere a un microtask para no reentrar en la
    // construcción del propio AuthService desde el authInterceptor
    // (`inject(AuthService)` durante el request).
    if (this._user() !== null && !this.isTokenExpired()) {
      queueMicrotask(() => this.refreshUser());
    }
  }

  /**
   * Refresca el user desde `GET /auth/me` y **sobrescribe** el guardado en
   * localStorage + el signal. Así `modulos` y `debeCambiarPassword` reflejan
   * el estado real del backend (cambios de rol, contraseña ya cambiada) sin
   * necesidad de re-login; el menú (navigation-loader) y `moduloGuard`/
   * `adminGuard` se recomputan al mover el signal. Un 401 ⇒ sesión inválida
   * ⇒ logout.
   */
  refreshUser(): void {
    this.http.get<User>(`${environment.apiUrl}/auth/me`).subscribe({
      next: (me) => {
        // Merge sobre el actual por si `/auth/me` omitiera algún campo local.
        const actualizado: User = { ...this._user(), ...me } as User;
        localStorage.setItem(USER_KEY, JSON.stringify(actualizado));
        this._user.set(actualizado);
      },
      error: (err: { status?: number }) => {
        // El authErrorInterceptor ya desloguea ante un 401 con token; lo
        // repetimos explícito por si esa ruta no estuviera activa.
        if (err?.status === 401) this.logout(true);
      },
    });
  }

  login(email: string, password: string): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(`${environment.apiUrl}/auth/login`, { email, password })
      .pipe(
        tap((res) => {
          localStorage.setItem(TOKEN_KEY, res.accessToken);
          localStorage.setItem(USER_KEY, JSON.stringify(res.user));
          localStorage.setItem(API_URL_KEY, environment.apiUrl);
          this._user.set(res.user);
        }),
      );
  }

  /**
   * Cambia la propia contraseña (`POST /auth/change-password`). Si el usuario
   * está en cambio obligatorio (`debeCambiarPassword`), el backend no exige la
   * contraseña actual — se omite. Al terminar, limpia el flag local.
   */
  cambiarPassword(passwordNueva: string, passwordActual?: string): Observable<void> {
    const body: { passwordNueva: string; passwordActual?: string } = { passwordNueva };
    if (passwordActual) body.passwordActual = passwordActual;
    return this.http
      .post<void>(`${environment.apiUrl}/auth/change-password`, body)
      .pipe(tap(() => this.markPasswordChanged()));
  }

  logout(redirectToLogin = true): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(API_URL_KEY);
    this._user.set(null);
    if (redirectToLogin) {
      // Full reload — más confiable que router.navigate() cuando el
      // logout viene desde un interceptor en medio de una navegación.
      window.location.href = '/login';
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    if (this.isTokenExpired(token)) return null;
    return token;
  }

  /** True si el JWT está vencido (o no se pudo parsear). */
  isTokenExpired(token?: string | null): boolean {
    const t = token ?? localStorage.getItem(TOKEN_KEY);
    if (!t) return true;
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      const expMs = (Number(payload?.exp) || 0) * 1000;
      if (expMs === 0) return true;
      return Date.now() + EXPIRY_LEEWAY_MS >= expMs;
    } catch {
      return true;
    }
  }

  private loadUser(): User | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  }

  /**
   * Detecta si el apiUrl actual difiere del guardado la última vez que se
   * hizo login. Si difiere, la sesión guardada pertenece a otro backend
   * (ej. pasaste de `npm run start:local` a `npm run start:dev`) y su
   * JWT está firmado con otro secret — no sirve. Limpiar antes de que
   * el interceptor lo intente usar y dispare logout automático.
   *
   * Devuelve true si limpió algo, para que el signal se inicialice en null.
   */
  private detectEnvSwitch(): boolean {
    const savedApiUrl = localStorage.getItem(API_URL_KEY);
    if (savedApiUrl && savedApiUrl !== environment.apiUrl) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(API_URL_KEY);
      console.info(
        `[auth] apiUrl cambió (${savedApiUrl} → ${environment.apiUrl}). Sesión anterior descartada.`,
      );
      return true;
    }
    return false;
  }
}
